import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { approveInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/approve-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import { updateShowOtaConfirmBackupProcessModal } from "@src/reducers/your-reducer";

export const useOtaConfirmBackupProcessModal = (
  data: ActiveInstallationProcessPayload,
  onClose: () => void,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );

  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const texts = {
    closeModalAriaLabel: i18n(
      "modules.otaConfirmBackupProcessModal.closeModalAriaLabel",
    ),
    title: i18n("modules.otaConfirmBackupProcessModal.title"),
    targetPathLabel: i18n(
      "modules.otaConfirmBackupProcessModal.targetPathLabel",
    ),
    sourceMachineLabel: i18n(
      "modules.otaConfirmBackupProcessModal.sourceMachineLabel",
    ),
    warningText: i18n("modules.otaConfirmBackupProcessModal.warningText"),
    cancelButtonText: i18n(
      "modules.otaConfirmBackupProcessModal.cancelButtonText",
    ),
    approveButtonText: i18n(
      "modules.otaConfirmBackupProcessModal.approveButtonText",
    ),
    approveSuccessMessage: i18n(
      "modules.otaConfirmBackupProcessModal.approveSuccessMessage",
    ),
    approveFailureMessage: i18n(
      "modules.otaConfirmBackupProcessModal.approveFailureMessage",
    ),
    approveErrorMessage: i18n(
      "modules.otaConfirmBackupProcessModal.approveErrorMessage",
    ),
  };

  const machine = {
    name: source?.name,
    ip: source?.dsn,
  };

  const targetPath = data?.target_path || "-";

  const handleCloseModal = () => {
    onClose();
    dispatch(updateShowOtaConfirmBackupProcessModal(false));
  };

  const handleApprove = async () => {
    if (!data?.update_id) {
      errorSnackbar(texts.approveErrorMessage);
      return;
    }

    setIsApproveLoading(true);

    try {
      const response = await approveInstallationProcessUseCase(data.update_id);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from approveInstallationProcessUseCase (backup confirmation modal):",
          response,
        );

      if (response.success) {
        successSnackbar(texts.approveSuccessMessage);
        handleCloseModal();
        return;
      }

      console.error(response.error);
      errorSnackbar(texts.approveFailureMessage);
      setIsApproveLoading(false);
    } catch (error) {
      console.error(error);
      errorSnackbar(texts.approveErrorMessage);
      setIsApproveLoading(false);
    }
  };

  return {
    texts,
    machine,
    targetPath,
    isApproveLoading,
    handleCloseModal,
    handleApprove,
  };
};
